export const ADMIN_AILIX_BASE_PATH = "/admin/ailix";
export const ADMIN_PM_BASE_PATH = "/admin/pm";
export const ADMIN_PM_CLIENTS_BASE_PATH = "/admin/pm/clients";
export const ADMIN_RH_BASE_PATH = "/admin/rh";
export const ADMIN_RIGHTS_BASE_PATH = "/admin/rights";

export const ADMIN_AILIX_SECTIONS = ["dashboard", "crm", "finance", "apporteurs"] as const;
export type AdminAilixSection = (typeof ADMIN_AILIX_SECTIONS)[number];
export const DEFAULT_ADMIN_AILIX_SECTION: AdminAilixSection = "dashboard";

export const ADMIN_PM_SECTIONS = ["kanban", "projets", "features"] as const;
export type AdminPMSection = (typeof ADMIN_PM_SECTIONS)[number];
export const DEFAULT_ADMIN_PM_SECTION: AdminPMSection = "kanban";

export const ADMIN_PM_CLIENT_SECTIONS = ["accounts", "guests", "visibility"] as const;
export type AdminPMClientSection = (typeof ADMIN_PM_CLIENT_SECTIONS)[number];
export const DEFAULT_ADMIN_PM_CLIENT_SECTION: AdminPMClientSection = "accounts";

/**
 * `ma-feuille` : ouvert par le poste à `<deviseur>/admin/rh/ma-feuille` (E2.4) — NE PAS renommer,
 * le poste ouvre cette adresse. `mon-releve` (lot B) : le relevé hebdomadaire à signer.
 * `mon-espace` (lot A) : les quatre files du salarié au même endroit.
 * Les trois sont visibles de tout salarié connecté, sans droit RH.
 * `etat-cii` (lot R, R.6) : l'État CII, visible du seul porteur d'`access_rd_statement`.
 */
export const ADMIN_RH_SECTIONS = [
  "salaries", "planning", "salle", "droits", "acces", "logs", "mon-espace", "ma-feuille", "mon-releve",
  "etat-cii",
] as const;
export type AdminRHSection = (typeof ADMIN_RH_SECTIONS)[number];
export const DEFAULT_ADMIN_RH_SECTION: AdminRHSection = "salaries";

export const LEGACY_ADMIN_RIGHTS_SECTIONS = [
  "internal-users",
  "permissions",
  "organizations",
  "access-logs",
  "banned-ips",
] as const;
export type LegacyAdminRightsSection = (typeof LEGACY_ADMIN_RIGHTS_SECTIONS)[number];
export const DEFAULT_LEGACY_ADMIN_RIGHTS_SECTION: LegacyAdminRightsSection = "internal-users";

export const adminAilixPath = (
  section: AdminAilixSection = DEFAULT_ADMIN_AILIX_SECTION
) => `${ADMIN_AILIX_BASE_PATH}/${section}`;

export const adminPMPath = (section: AdminPMSection = DEFAULT_ADMIN_PM_SECTION) =>
  `${ADMIN_PM_BASE_PATH}/${section}`;

/** Clé de l'onglet « À valider » de la page Features : le poste ouvre exactement cette URL. */
export const FEATURES_REVIEW_TAB = "a-valider";
export const adminFeaturesReviewPath = () =>
  `${adminPMPath("features")}?tab=${FEATURES_REVIEW_TAB}`;

/**
 * Le même onglet, borné aux features qui portent une question sans réponse (lot A, carte
 * « Questions de l'IA »). Un filtre de l'onglet, pas un onglet de plus : les questions se
 * répondent sur la feature qui les porte.
 */
export const FEATURES_QUESTIONS_FILTER = "questions";
export const adminFeaturesQuestionsPath = () =>
  `${adminFeaturesReviewPath()}&filtre=${FEATURES_QUESTIONS_FILTER}`;

/**
 * Chaque tableau du Kanban a son URL : `/admin/pm/kanban/<clé>`. La clé est l'id du tableau,
 * ou sa `system_key` pour un tableau système (ex. `features_tracking`), stable d'un
 * environnement à l'autre là où l'UUID ne l'est pas.
 */
export const adminPMKanbanBoardPath = (boardKey: string) =>
  `${adminPMPath("kanban")}/${encodeURIComponent(boardKey)}`;

/** La clé de tableau portée par l'URL du Kanban, null sur `/admin/pm/kanban` nu. */
export const getAdminPMKanbanBoardKeyFromPathname = (pathname: string): string | null => {
  const basePath = `${adminPMPath("kanban")}/`;
  if (!pathname.startsWith(basePath)) {
    return null;
  }
  const key = pathname.slice(basePath.length).split("/")[0];
  if (!key) {
    return null;
  }
  try {
    return decodeURIComponent(key);
  } catch {
    return key;
  }
};

export const adminPMClientsPath = (
  section: AdminPMClientSection = DEFAULT_ADMIN_PM_CLIENT_SECTION
) => `${ADMIN_PM_CLIENTS_BASE_PATH}/${section}`;

export const adminRHPath = (section: AdminRHSection = DEFAULT_ADMIN_RH_SECTION) =>
  `${ADMIN_RH_BASE_PATH}/${section}`;

export const adminOrganizationKanbanPath = (orgId: string, projectId?: string | null) => {
  const basePath = `/admin/${orgId}/kanban`;
  if (!projectId) {
    return basePath;
  }

  const searchParams = new URLSearchParams({ projectId });
  return `${basePath}?${searchParams.toString()}`;
};

export const isOneOf = <T extends string>(
  value: string | null,
  values: readonly T[]
): value is T => {
  return value !== null && (values as readonly string[]).includes(value);
};

export const getAdminSectionFromPathname = (pathname: string, basePath: string) => {
  if (pathname === basePath || pathname === `${basePath}/`) {
    return null;
  }

  const normalizedBasePath = basePath.endsWith("/") ? basePath : `${basePath}/`;
  if (!pathname.startsWith(normalizedBasePath)) {
    return null;
  }

  const section = pathname.slice(normalizedBasePath.length).split("/")[0];
  return section || null;
};

export const mapLegacyAdminRightsSectionToRH = (
  section: LegacyAdminRightsSection = DEFAULT_LEGACY_ADMIN_RIGHTS_SECTION
): AdminRHSection => {
  switch (section) {
    case "internal-users":
    case "permissions":
      return "droits";
    case "organizations":
    case "access-logs":
    case "banned-ips":
      return "acces";
  }
};
